import { BrandLogoEntityRaw, BrandLogoIsInUse } from "@common/domain";
import { EntityState, EntityAdapter, createEntityAdapter } from "@ngrx/entity";
import { createReducer, on, Action } from "@ngrx/store";
import { flatMap } from "lodash";
import { BrandLogoActions, BrandActions } from "../actions";

export const BRAND_LOGO_FEATURE_KEY = "brandLogo";

export interface BrandLogoState extends EntityState<BrandLogoEntityRaw> {}

export const brandLogoAdapter: EntityAdapter<BrandLogoEntityRaw> =
  createEntityAdapter<BrandLogoEntityRaw>({
    selectId: (_) => _.id,
    sortComparer: (lhs, rhs) => {
      if (lhs.isDefault && rhs.isDefault) {
        return lhs.id - rhs.id;
      }

      if (lhs.isDefault) {
        return -1;
      }

      if (rhs.isDefault) {
        return 1;
      }

      return lhs.id - rhs.id;
    }
  });

export const initialBrandLogoState: BrandLogoState =
  brandLogoAdapter.getInitialState({});

const reducer = createReducer(
  initialBrandLogoState,
  on(BrandActions.brandGetListSuccess, (state, { brands }) =>
    brandLogoAdapter.addMany(
      flatMap(brands, (_) => _.value.logos),
      state
    )
  ),
  on(BrandActions.brandCreateSuccess, (state, { brand }) => {
    return brandLogoAdapter.addMany(brand.value.logos, state);
  }),
  on(BrandActions.brandDeleteSuccess, (state, { brandId }) => {
    const logos = Object.values(state.entities).filter(
      (_) => _?.brandId === brandId
    );

    return brandLogoAdapter.removeMany(
      logos.map((_) => _!.id),
      state
    );
  }),
  on(BrandLogoActions.brandLogoCreateSuccess, (state, { brandLogo }) =>
    brandLogoAdapter.addOne(brandLogo.toJSON(), state)
  ),
  on(BrandLogoActions.brandLogoDeleteSuccess, (state, { brandLogo }) =>
    brandLogoAdapter.removeOne(brandLogo.value.id, state)
  ),
  on(BrandLogoActions.brandLogoUnUseSuccess, (state, { brandLogo }) =>
    brandLogoAdapter.updateOne(
      {
        id: brandLogo.value.id,
        changes: { isInUse: false as BrandLogoIsInUse }
      },
      state
    )
  ),
  on(BrandLogoActions.brandLogoUseSuccess, (state, { brandLogo }) => {
    const logos = Object.values(state.entities).filter(
      (_) => _?.brandId === brandLogo.value.brandId
    );

    return brandLogoAdapter.updateOne(
      {
        id: brandLogo.value.id,
        changes: { isInUse: true as BrandLogoIsInUse }
      },
      brandLogoAdapter.updateMany(
        logos.map((_) => ({
          id: _!.id,
          changes: { isInUse: false as BrandLogoIsInUse }
        })),
        state
      )
    );
  })
);

export function brandLogoReducer(
  state: BrandLogoState | undefined,
  action: Action
) {
  return reducer(state, action);
}
